// ContactForm.tsx
import { useEffect, useRef, useState } from "react";
import { useFetcher, useRouteLoaderData, type LinksFunction } from "react-router";
import MessageModal from "~/layout/public/message-modal";
import type { loader as siteLayoutLoader } from "~/layout/public/site-layout";
import type { ContactFormProps }  from "~/models/contactFormProps";
import styles from './contact.css?url';

const HP_NAME = import.meta.env.VITE_HP_NAME; // uncommon field name for honeypot

export default function ContactForm({ formRef, formSig, subjectOverride }: ContactFormProps) {
  const data = useRouteLoaderData<typeof siteLayoutLoader>("site-layout");
  const thankYouMsg =
    "Thank you for your enquiry. <br /><br />Someone will be in touch with you soon.<br /><br />In the meantime, please continue to browse our website to see what services we can offer you.";
  const errMsg = `We are really sorry but an error has occurred submitting the form.<br /><br />Please try again later, or contact us directly on ${data?.phoneNumber}`;

  const fetcher = useFetcher<{ ok?: boolean; error?: string }>();
  const tokenFetcher = useFetcher<{ ts: string }>(); // NEW
  const [token, setToken] = useState<string>("");     // NEW

  useEffect(() => {
    // Grab a fresh token & set HttpOnly cookie for timing validation
    if (tokenFetcher.state === "idle" && !tokenFetcher.data) {
      tokenFetcher.load("/_resources/form-token");
    }
  }, [tokenFetcher]);

  useEffect(() => {
    if (tokenFetcher.data?.ts) setToken(tokenFetcher.data.ts);
  }, [tokenFetcher.data]);

  const isSubmitting = fetcher.state !== "idle";
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const formEl = useRef<HTMLFormElement>(null);

  // When the submission finishes, open the right modal
  useEffect(() => {
    if (fetcher.state === "idle" && fetcher.data) {
      if (fetcher.data.ok) {
        setShowSuccess(true);
        // Reset the form fields after a successful submit
        formEl.current?.reset();
      } else {
        setShowError(true);
      }
    }
  }, [fetcher.state, fetcher.data]);

  return (
    <div className={subjectOverride ? "" : "col-lg-8 order-lg-2 order-1 mb-4 mb-lg-0"}>
      {subjectOverride ? <h3 className="primary">Enquire about {subjectOverride}</h3> : null}

      <fetcher.Form method="post" action="/_actions/submit" ref={formEl}>
        {/* identify which form this is */}
        <input type="hidden" name="formRef" value={formRef} />
        <input type="hidden" name="formSig" value={formSig} />

         {/* NEW: timing token paired with HttpOnly cookie from the resource loader */}
        <input type="hidden" name="ts" value={token} />

        {/* NEW: honeypot (keep accessible; hide visually) */}
        <label htmlFor={HP_NAME} className="sr-only">Leave this field empty</label>
        <input
          id={HP_NAME}
          name={HP_NAME}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: "absolute", left: "-9999px", width: 0, height: 0, opacity: 0 }}
        />

        {/* pass through a subject if the page sets one */}
        {subjectOverride && (
          <input type="hidden" name="subject" value={subjectOverride} autoComplete="off" />
        )}

        <div className="row gy-4">
          <div className="col-md-6">
            <input type="text" name="name" className="form-control" placeholder="Full Name" required autoComplete="off" />
          </div>

          <div className="col-md-6">
            <input type="email" name="email" className="form-control" placeholder="Email" required autoComplete="off" />
          </div>

          {!subjectOverride && (
            <div className="col-md-12">
              <input type="text" name="subject" className="form-control" placeholder="Subject" autoComplete="off" />
            </div>
          )}

          <div className="col-md-12">
            <textarea name="message" rows={6} placeholder="Message" className="form-control" required autoComplete="off" />
          </div>

          <div>
            <button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </div>
        </div>
      </fetcher.Form>

      <MessageModal
        title="Thank you"
        message={thankYouMsg}
        show={showSuccess}
        handleClose={() => setShowSuccess(false)}
      />
      <MessageModal
        title="Sorry"
        message={errMsg}
        show={showError}
        handleClose={() => setShowError(false)}
      />
    </div>
  );
}

export const links: LinksFunction = () => [
    { rel: "stylesheet", href: styles },
];
// ContactForm.tsx